import { memo, useCallback } from "react";
import { useWatch } from "react-hook-form";
import { SimpleRangeSlider } from "@/components/base/simple-range-slider";
import { LabelWithTooltip } from "@/components/filters/shared";
import type { useFilters } from "@/providers/filter-provider";

type SellerRatingRangeFilterProps = {
  form: ReturnType<typeof useFilters>["form"];
};

export const SellerRatingRangeFilter = memo(({ form }: SellerRatingRangeFilterProps) => {
  const rangeMin = 0;
  const rangeMax = 5;

  // Use useWatch for efficient subscriptions to specific fields
  const minRating = useWatch({ control: form.control, name: "minRating" });
  const maxRating = useWatch({ control: form.control, name: "maxRating" });

  const minValue = Number(minRating) || rangeMin;
  const maxValue = Number(maxRating) || rangeMax;

  const handleMinChange = useCallback(
    (value: number) => form.setValue("minRating", String(value), { shouldDirty: true, shouldTouch: true }),
    [form],
  );

  const handleMaxChange = useCallback(
    (value: number) => form.setValue("maxRating", String(value), { shouldDirty: true, shouldTouch: true }),
    [form],
  );

  return (
    <div className="flex flex-col gap-2">
      <LabelWithTooltip
        label="Seller Rating"
        tooltip="Seller Rating"
        description="Rating from sellers who have worked with this creator."
      />
      <SimpleRangeSlider
        min={rangeMin}
        max={rangeMax}
        minValue={Math.min(minValue, maxValue)}
        maxValue={Math.max(maxValue, minValue)}
        onMinChange={handleMinChange}
        onMaxChange={handleMaxChange}
        format="number"
        step={0.1}
      />
    </div>
  );
});
